import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import CountUp from "react-countup";
import Marquee from "react-fast-marquee";
import {
  Sun, Zap, Battery, Wind, Flame, Snowflake,
  Factory, Car, UtensilsCrossed, Shirt, Building2, Package,
  ChevronRight, ArrowRight, DollarSign, TrendingDown,
  Shield, Leaf, Clock,
} from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f79a6853-bb27-42e4-97ca-1ec91afb31f3/images/09b2d36b272d1ed90c7058d740f859a00f2b26b803cfb1649756567150adcb1c.png";

const stats = [
  { value: 30, suffix: " MW+", label: "Portfolio" },
  { value: 32000, suffix: "+", label: "Tonnes CO\u2082 Reduced/Year" },
  { value: 100, prefix: "$", suffix: "Mn+", label: "Committed Investment" },
  { value: 25, suffix: "+ Year", label: "PPAs" },
  { value: 10, suffix: "+", label: "States" },
];

const painPoints = [
  { icon: DollarSign, pain: "High Energy Costs", solution: "Switch to solar and save 30-60% on electricity bills from Day 1 \u2014 with zero upfront investment through our RESCO model." },
  { icon: TrendingDown, pain: "Rising Tariffs", solution: "Lock in fixed PPA rates for up to 25 years. No more budget surprises from volatile grid tariffs." },
  { icon: Shield, pain: "CapEx Barriers", solution: "Zero CapEx required. EnergyX funds, builds, owns, and operates. You simply consume cleaner, cheaper power." },
  { icon: Clock, pain: "Downtime & Maintenance", solution: "Our professional O&M teams ensure 99%+ uptime with real-time monitoring and preventive maintenance." },
  { icon: Leaf, pain: "ESG & Compliance Pressure", solution: "Generate verifiable green energy credentials. Meet sustainability targets with documented CO\u2082 reduction." },
];

const services = [
  { icon: Sun, title: "Solar Energy", desc: "Onsite rooftop & ground mount systems. Zero CapEx, savings from Day 1.", link: "/services" },
  { icon: Zap, title: "Open Access Solar", desc: "MW-scale solar parks across Haryana, Rajasthan, UP, Maharashtra & Gujarat.", link: "/services" },
  { icon: Battery, title: "BESS", desc: "Large-scale battery storage for peak shaving, grid balancing & backup.", link: "/services" },
  { icon: Wind, title: "Wind Energy", desc: "Offsite wind power solutions for large C&I consumers.", link: "/services" },
  { icon: Flame, title: "Industrial Heat", desc: "Biomass-based thermal solutions for process heating.", link: "/services" },
  { icon: Snowflake, title: "Cooling Solutions", desc: "Greenfield & retrofit cooling for industrial facilities.", link: "/services" },
];

const sectors = [
  { icon: Factory, name: "Manufacturing" },
  { icon: Car, name: "Automotive" },
  { icon: UtensilsCrossed, name: "F&B Processing" },
  { icon: Shirt, name: "Textile" },
  { icon: Building2, name: "Hospitality" },
  { icon: Package, name: "Packaging & Paper" },
];

const customers = [
  "Imperial Auto", "Amber Group", "Lemon Tree Hotels", "F-Tech Japan",
  "VG Automotive", "Victura Group", "Premier", "ACCIL",
  "Lion Fabrics", "Omniplast", "Sona Beverages", "CMI Limited",
  "NKM Cables", "Shivmani Exports", "Anya Polytech", "Spectrum Resort",
  "Orient Kagaz", "Bajaj Motors",
];

const featuredProjects = [
  { client: "Victura Group", capacity: "6,000 kW", location: "Pan India", img: "https://images.pexels.com/photos/9799994/pexels-photo-9799994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { client: "Amber Group", capacity: "5,000 kW", location: "Pan India", img: "https://images.unsplash.com/photo-1726866492047-7f9516558c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwc29sYXIlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzc3MzYyMzcxfDA&ixlib=rb-4.1.0&q=85" },
  { client: "Imperial Auto", capacity: "2,650 kW", location: "Bawal, HR", img: "https://static.prod-images.emergentagent.com/jobs/f79a6853-bb27-42e4-97ca-1ec91afb31f3/images/58f89974e93b472b880c66ce9088dddaf55b5b7c98ffd61536af91eb39709f1f.png" },
  { client: "ACCIL", capacity: "2,600 kW", location: "3 Sites, HR & RJ", img: "https://images.pexels.com/photos/33438126/pexels-photo-33438126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

function StatCounter({ stat, inView }) {
  return (
    <div className="text-center">
      <div className="text-2xl md:text-3xl font-black text-[#00D68F]">
        {stat.prefix}
        {inView ? <CountUp end={stat.value} duration={2.5} separator="," /> : 0}
        {stat.suffix}
      </div>
      <div className="text-white/60 text-xs mt-1">{stat.label}</div>
    </div>
  );
}

export default function HomePage() {
  const [activePain, setActivePain] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  return (
    <div>
      {/* ── HERO ──────────────────────────────────────────── */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-[#0A1628]/70" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="aurora-mesh" />
        </div>
        {/* Mouse-follow glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(0,214,143,0.08) 0%, transparent 70%)",
            left: mousePos.x - 200,
            top: mousePos.y - 200,
            transition: "left 0.3s ease-out, top 0.3s ease-out",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-6">
              Re-powering the Future
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[0.95] max-w-4xl">
              India's Premier Solar
              <br />
              <span className="text-[#00D68F]">Asset Developer</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Zero CapEx. Clean energy from Day 1. We build, own, and operate
              {" \u2014 "}so you save 30-60% on energy costs.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link
                to="/services"
                data-testid="hero-explore-btn"
                className="bg-[#00D68F] text-[#0A1628] px-8 py-4 font-bold hover:-translate-y-1 transition-all inline-flex items-center gap-2"
              >
                Explore Our Solutions <ArrowRight size={18} />
              </Link>
              <Link
                to="/projects"
                data-testid="hero-portfolio-btn"
                className="border border-white/20 text-white px-8 py-4 font-bold hover:bg-white hover:text-[#0A1628] transition-all"
              >
                View Our Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
        {/* Stats bar */}
        <div
          ref={statsRef}
          className="absolute bottom-0 left-0 right-0 bg-[#0A1628]/80 backdrop-blur-md border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-5 gap-6">
            {stats.map((s, i) => (
              <StatCounter key={i} stat={s} inView={statsInView} />
            ))}
          </div>
        </div>
      </section>

      {/* ── PAIN POINTS ───────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-white"
        data-testid="pain-points-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Why Switch?
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-16">
            Your Challenges.
            <br />
            Our Solutions.
          </h2>
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-4 flex flex-col gap-2">
              {painPoints.map((p, i) => {
                const Icon = p.icon;
                return (
                  <button
                    key={i}
                    onClick={() => setActivePain(i)}
                    data-testid={`pain-tab-${i}`}
                    className={`text-left px-6 py-4 transition-all flex items-center gap-3 ${
                      activePain === i
                        ? "bg-[#0A1628] text-white"
                        : "text-[#0A1628]/70 hover:bg-[#F5F7FA]"
                    }`}
                  >
                    <Icon
                      size={20}
                      className={activePain === i ? "text-[#00D68F]" : ""}
                    />
                    <span className="font-bold text-sm">{p.pain}</span>
                  </button>
                );
              })}
            </div>
            <div className="md:col-span-8 bg-[#F5F7FA] p-8 md:p-12 flex items-center">
              <motion.div
                key={activePain}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
              >
                {(() => {
                  const Icon = painPoints[activePain].icon;
                  return (
                    <>
                      <div className="flex items-center gap-3 mb-4">
                        <Icon size={32} className="text-[#00D68F]" />
                        <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628]">
                          {painPoints[activePain].pain}
                        </h3>
                      </div>
                      <p className="text-[#0A1628]/70 text-base md:text-lg leading-relaxed">
                        {painPoints[activePain].solution}
                      </p>
                    </>
                  );
                })()}
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── RESCO FLOW ────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-[#0A1628]"
        data-testid="resco-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Our Model
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Zero CapEx. We Build. You Save.
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mb-16">
            Our RESCO (Renewable Energy Service Company) model eliminates
            upfront costs. We invest, install, and operate — you consume
            cheaper, cleaner power.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { step: "01", title: "Investment", desc: "We secure funding from global climate investors" },
              { step: "02", title: "Installation", desc: "EnergyX designs & builds your solar system" },
              { step: "03", title: "Power Generation", desc: "Your facility generates clean solar energy" },
              { step: "04", title: "Consumption", desc: "You pay only for the power you consume" },
              { step: "05", title: "Savings", desc: "Save 30-60% vs grid tariffs for 25 years" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 md:p-8 relative group hover:bg-white/10 transition-colors"
              >
                <span className="text-[#00D68F] text-xs font-bold tracking-wider">
                  {item.step}
                </span>
                <h4 className="text-white font-bold mt-3 mb-2">{item.title}</h4>
                <p className="text-white/50 text-sm">{item.desc}</p>
                {i < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-[2px] bg-[#00D68F]/40" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ── SERVICES GRID ─────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-white"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            What We Offer
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-16">
            Comprehensive Energy Solutions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white border border-gray-200 p-8 group hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid={`service-card-${i}`}
                >
                  <Icon size={28} className="text-[#00D68F] mb-4" />
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">
                    {s.title}
                  </h3>
                  <p className="text-[#0A1628]/60 text-sm leading-relaxed mb-4">
                    {s.desc}
                  </p>
                  <Link
                    to={s.link}
                    className="text-[#00D68F] text-sm font-bold inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                  >
                    Learn More <ChevronRight size={14} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* ── SECTORS ───────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-[#F5F7FA]"
        data-testid="sectors-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Industries
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-16">
            Sectors We Serve
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            {sectors.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white border border-gray-200 p-6 text-center group hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <Icon
                    size={32}
                    className="mx-auto text-[#0A1628]/40 group-hover:text-[#00D68F] transition-colors mb-3"
                  />
                  <span className="text-sm font-bold text-[#0A1628]">
                    {s.name}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* ── CUSTOMER LOGOS ────────────────────────────────── */}
      <section className="py-24 md:py-32 bg-[#0A1628]" data-testid="logos-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Trusted By
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white">
            Industry Leaders
          </h2>
        </div>
        <Marquee speed={40} gradient={false} className="py-4">
          {customers.map((c, i) => (
            <div
              key={i}
              className="mx-8 px-6 py-3 border border-white/10 text-white/40 text-sm font-bold tracking-wider hover:text-[#00D68F] hover:border-[#00D68F]/30 transition-colors cursor-default whitespace-nowrap"
            >
              {c}
            </div>
          ))}
        </Marquee>
      </section>

      {/* ── FEATURED PROJECTS ─────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7 }}
        className="py-24 md:py-32 bg-white"
        data-testid="featured-projects-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Portfolio
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-16">
            Featured Projects
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredProjects.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="group relative overflow-hidden h-80"
                data-testid={`featured-project-${i}`}
              >
                <img
                  src={p.img}
                  alt={p.client}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <span className="text-[#00D68F] text-sm font-bold">
                    {p.capacity}
                  </span>
                  <h3 className="text-white text-2xl font-bold mt-1">
                    {p.client}
                  </h3>
                  <p className="text-white/60 text-sm">{p.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/projects"
              data-testid="view-all-projects"
              className="bg-[#00D68F] text-[#0A1628] px-8 py-4 font-bold hover:-translate-y-1 transition-all inline-flex items-center gap-2"
            >
              View All Projects <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </motion.section>

      {/* ── CTA BANNER ────────────────────────────────────── */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/f79a6853-bb27-42e4-97ca-1ec91afb31f3/images/2c38538cf59bfe08c2b395afc8d6323aeee8dfcb216fd9c22f5363d678aff8d3.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-[#0A1628]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Ready to cut your energy costs by{" "}
            <span className="text-[#00D68F]">30-60%</span>?
          </h2>
          <p className="text-white/60 text-base md:text-lg mb-10">
            Let's talk about how EnergyX can power your business with clean,
            affordable solar energy.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact"
            className="bg-[#00D68F] text-[#0A1628] px-10 py-4 font-bold hover:-translate-y-1 transition-all inline-flex items-center gap-2 text-lg"
          >
            Get Started <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
